/**
 * Auth API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 * Bearer tokens are Clerk JWTs only; no legacy login/register/refresh.
 */

import { apiTransport as router, getApiOrigin } from './transport';
import { getContext } from './core';
import { getClerkToken } from './clerkAuth';
import type { AuthUser } from '@repo/shared-types/auth';

/** Trust accounting step from PM signup (operating required; trust optional for SMO). */
export interface TrustAccountingStepData {
  operating: { bankName: string; accountNumber: string; routingNumber: string };
  trust?: { bankName: string; accountNumber: string; routingNumber: string };
  complianceAcknowledged?: boolean;
}

/** PM trust step when bank details were tokenized via Plaid (pre-profile). */
export interface TrustAccountingPlaidData {
  operating: { processorToken: string; bankName?: string };
  trust?: { processorToken: string; bankName?: string };
}

/** Identity sent with onboarding payload so backend does not branch on step keys. */
export interface OnboardingIdentityPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** Payload for completing Clerk sign-up after Join the Ecosystem (backend creates UserProfile and syncs metadata). */
export interface CompleteClerkOnboardingData {
  role: 'PROPERTY_MANAGER' | 'OWNER' | 'VENDOR' | 'INVESTOR' | 'TENANT';
  isSelfManagingOwner: boolean;
  /** When present, backend uses this for UserProfile instead of stepData/Clerk. Prevents per-role key branching. */
  identity?: OnboardingIdentityPayload;
  companyName?: string;
  managementApproach?: 'self-manage-all' | 'pm-for-all' | 'mixed';
  workflowPreferences?: { monthlyReports?: boolean; maintenanceAlerts?: boolean };
  trustAccounting?: TrustAccountingStepData;
  trustAccountingPlaid?: TrustAccountingPlaidData;
  /** Wizard stepData for Investor (entity/accreditation/bank) or other roles that need it. */
  stepData?: Record<string, unknown>;
  /** Pre-signup upload token from migration flow; migration runs after account creation */
  upload_token?: string;
  /** Server-side wizard snapshot from POST /onboarding/clerk-handoff */
  handoff_token?: string;
  onboardingConsent?: {
    termsVersion: string;
    privacyVersion: string;
    marketingOptIn?: boolean;
    source: string;
    termsAccepted: true;
  };
}

export const authApi = {
  /** After Clerk sign-up from Join the Ecosystem: create UserProfile and set Clerk publicMetadata. Requires Clerk Bearer token. */
  completeClerkOnboarding: async (data: CompleteClerkOnboardingData) =>
    router.post<AuthUser>('/auth/complete-clerk-onboarding', data),
  /** Persist wizard payload server-side (JWT required). Returns opaque `handoff_token` for `/auth/complete-clerk-onboarding`. */
  createClerkOnboardingHandoff: async (body: Record<string, unknown>) =>
    router.post<{ handoff_token: string }>('/onboarding/clerk-handoff', body),
  logout: async () => router.post<{ success: boolean }>('/auth/logout', {}, getContext()),
  getCurrentUser: async () => router.get<AuthUser>('/auth/current-user', getContext()),
  /** Probe for existing profile without triggering 401 redirect (e.g. on /complete-onboarding when no pending data). Returns null on 401. */
  getCurrentUserIfExists: async (): Promise<AuthUser | null> => {
    const token = await getClerkToken();
    if (!token) return null;
    const origin = getApiOrigin();
    // Do not send Zustand `context` here: over HTTP the API resolves identity from `Authorization`
    // only (`resolveContext` ignores JSON body context when a Bearer token is present). Including
    // stale `userRole` / `currentUserId` was misleading and implied a coupling that does not exist.
    const res = await fetch(`${origin}/api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        method: 'GET',
        path: '/auth/current-user',
      }),
    });
    if (res.status === 401) return null;
    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        return null;
      }
    }
    if (!res.ok) return null;
    return parsed as AuthUser;
  },
  getEntityId: async (userId: string, userRole: string) => {
    const res = await router.get<{ entityId: string }>('/auth/entity-id', getContext(), { userId, userRole });
    return res?.entityId ?? null;
  },
  /** Sync create OperatingOrganization + Clerk org + memberships (operator roles only). */
  createOperatorWorkspace: async (body: { name: string }) =>
    router.post<{ operatingOrganizationId: string; clerkOrganizationId: string }>(
      '/auth/operator-workspaces',
      body,
      getContext()
    ),
  /** JIT org capability hints for the current user (no JWT boolean bloat). */
  getOperatorOrgCapabilities: async (operatingOrganizationId: string) =>
    router.get<{
      operatingOrganizationId: string;
      organizationMemberRole: string;
      globalOperator: boolean;
      isOrgAdmin: boolean;
      isOrgManager: boolean;
      canConfigureApprovalThresholds: boolean;
      stripeConnect: {
        hasConnectAccount: boolean;
        connectOnboardingCompleted: boolean;
        chargesEnabled: boolean | null;
        payoutsEnabled: boolean | null;
        moneyRailInconsistent: boolean;
      };
    }>('/auth/operator-org-capabilities', getContext(), { operatingOrganizationId }),
};

